import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, ImageReveal } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { useSite } from "@/context/SiteContext";
import SmartLink from "@/components/SmartLink";

function MultiLine({ text }) {
  return (text || "").split("\n").map((ln, i) => <span key={i} className="block">{ln}</span>);
}

export default function About() {
  const site = useSite();
  const a = site.about || {};
  const chapters = a.chapters || [];

  return (
    <div data-testid="about-page" className="min-h-screen bg-white pt-20">
      {/* hero */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 pt-16 md:pt-24 grain">
        <Reveal>
          <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">{a.eyebrow}</p>
          <h1 className="mt-4 font-heading text-6xl font-black uppercase leading-[0.82] tracking-tighter md:text-[11vw]">
            {a.title}<br /><span className="text-gavra">{a.title_accent}</span>
          </h1>
        </Reveal>
        {a.hero_image && (
          <ImageReveal className="mt-12 aspect-[21/9] overflow-hidden border border-black/10">
            <img src={a.hero_image} alt="GAVRA" className="h-full w-full object-cover" />
          </ImageReveal>
        )}
      </section>

      {/* intro */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 py-20 md:py-28">
        <Reveal>
          <p className="max-w-4xl font-heading text-2xl font-bold uppercase leading-tight tracking-tight md:text-4xl">
            {a.intro}
          </p>
        </Reveal>
      </section>

      {/* chapters */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 pb-20 md:pb-28">
        <div className="space-y-16 md:space-y-24">
          {chapters.map((c, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="grid grid-cols-1 gap-6 border-t-2 border-black pt-8 md:grid-cols-12 md:gap-10">
                <div className="md:col-span-4">
                  <span className="font-heading text-8xl font-black leading-none tracking-tighter text-transparent md:text-9xl" style={{ WebkitTextStroke: "2px #FF5A00" }}>
                    {c.n}
                  </span>
                </div>
                <div className="md:col-span-8">
                  <h2 className="font-heading text-3xl font-extrabold uppercase tracking-tight md:text-5xl">{c.title}</h2>
                  <p className="mt-5 max-w-2xl text-base leading-relaxed text-black/60 md:text-lg">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee text={["No Compromise", "Precision Engineered", "Built To Last"]} speed={40} />

      {/* CTA */}
      <section className="bg-black text-white grain">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10 py-24 text-center md:py-32">
          <Reveal>
            <h2 className="font-heading text-4xl font-black uppercase leading-none tracking-tighter md:text-7xl">
              <MultiLine text={a.cta_title} />
            </h2>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <SmartLink to={a.cta_primary_url} testid="about-cta-primary" className="inline-flex items-center gap-3 bg-gavra px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white hover:text-black">
                {a.cta_primary_label} <ArrowRight className="h-4 w-4" />
              </SmartLink>
              <SmartLink to={a.cta_secondary_url} testid="about-cta-secondary" className="inline-flex items-center gap-2 border border-white/30 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white hover:text-black">
                {a.cta_secondary_label}
              </SmartLink>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
